import type { WorkingPattern } from '@/types/timesheet';
import { prefixKey } from '@/utils/storagePrefix';

const STORAGE_KEY = prefixKey('timetrack_working_patterns');
const ORG_DEFAULTS_KEY = prefixKey('timetrack_org_working_pattern');

interface WorkingPatternStorage {
  [employeeId: string]: WorkingPattern;
}

/**
 * Service for managing working patterns in localStorage
 * This provides persistence for the demo/prototype without needing a backend
 */
export const workingPatternService = {
  /**
   * Get working pattern for a specific employee
   */
  getEmployeePattern(employeeId: string): WorkingPattern | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      
      const patterns: WorkingPatternStorage = JSON.parse(stored);
      return patterns[employeeId] || null;
    } catch (error) {
      console.error('Error reading working pattern from localStorage:', error);
      return null;
    }
  },

  /**
   * Save working pattern for a specific employee
   */
  saveEmployeePattern(employeeId: string, pattern: WorkingPattern): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const patterns: WorkingPatternStorage = stored ? JSON.parse(stored) : {};
      
      patterns[employeeId] = pattern;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(patterns));
    } catch (error) {
      console.error('Error saving working pattern to localStorage:', error);
      throw new Error('Failed to save working pattern');
    }
  },

  /**
   * Delete working pattern for a specific employee (reverts to org default)
   */
  deleteEmployeePattern(employeeId: string): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      
      const patterns: WorkingPatternStorage = JSON.parse(stored);
      delete patterns[employeeId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(patterns));
    } catch (error) {
      console.error('Error deleting working pattern from localStorage:', error);
      throw new Error('Failed to delete working pattern');
    }
  },

  /**
   * Get organisation default working pattern
   */
  getOrgDefault(): WorkingPattern {
    try {
      const stored = localStorage.getItem(ORG_DEFAULTS_KEY);
      if (!stored) {
        // Return default 40-hour, Mon-Fri pattern
        return {
          hoursPerWeek: 40,
          workingDays: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: false,
            sunday: false
          }
        };
      }
      
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error reading org default from localStorage:', error);
      // Return default on error
      return {
        hoursPerWeek: 40,
        workingDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false
        }
      };
    }
  },

  /**
   * Save organisation default working pattern
   */
  saveOrgDefault(pattern: WorkingPattern): void {
    try {
      localStorage.setItem(ORG_DEFAULTS_KEY, JSON.stringify(pattern));
    } catch (error) {
      console.error('Error saving org default to localStorage:', error);
      throw new Error('Failed to save organisation default');
    }
  },

  /**
   * Get effective working pattern for an employee
   * Returns employee-specific pattern if set, otherwise org default
   */
  getEffectivePattern(employeeId: string): WorkingPattern {
    const employeePattern = this.getEmployeePattern(employeeId);
    return employeePattern || this.getOrgDefault();
  },

  /**
   * Check if employee has a custom pattern (not using org default)
   */
  hasCustomPattern(employeeId: string): boolean {
    return this.getEmployeePattern(employeeId) !== null;
  },

  /**
   * Get all employee patterns
   */
  getAllEmployeePatterns(): WorkingPatternStorage {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error reading all patterns from localStorage:', error);
      return {};
    }
  },

  /**
   * Clear all working patterns (for testing/reset)
   */
  clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(ORG_DEFAULTS_KEY);
    } catch (error) {
      console.error('Error clearing working patterns from localStorage:', error);
    }
  }
};
